import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth, useApi } from '@/contexts/AuthContext';
import { useClassFilter } from '@/contexts/ClassFilterContext';
import { fetchPriorities } from '@/lib/priorityFeed';
import { Search, Bell, ChevronDown, Filter, X } from 'lucide-react';

const INSURANCE_CLASSES = [
  { id: 'all', label: 'All Classes' },
  { id: 'sme_commercial', label: 'SME Commercial' },
  { id: 'commercial_motor', label: 'Commercial Motor' },
  { id: 'heavy_motor', label: 'Heavy Motor / Fleet' },
  { id: 'motor_trade', label: 'Motor Trade' },
  { id: 'public_liability', label: 'Public Liability' },
  { id: 'professional_indemnity', label: 'Professional Indemnity' },
  { id: 'management_liability', label: 'Management Liability' },
  { id: 'cyber', label: 'Cyber' },
  { id: 'home_contents', label: 'Home & Contents' },
  { id: 'personal_motor', label: 'Prestige & Specialty Motor' },
  { id: 'residential_landlords', label: 'Residential Landlords' },
  { id: 'commercial_landlords', label: 'Commercial Landlords' },
  { id: 'strata', label: 'Body Corporate / Strata' },
  { id: 'farm', label: 'Farm' },
  { id: 'construction', label: 'Construction' },
  { id: 'trade_credit', label: 'Trade Credit' },
  { id: 'group_personal_accident', label: 'Group Personal Accident' },
  { id: 'plant_equipment', label: 'Plant & Equipment' },
  { id: 'tools_equipment', label: 'Tools & Equipment' },
];

export default function TopBar({ onOpenSearch }) {
  const { user } = useAuth();
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [classOpen, setClassOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const { classFilter: globalClass, setClass: setGlobalClass } = useClassFilter();
  const currentClass = globalClass || 'all';
  const currentClassLabel = INSURANCE_CLASSES.find(c => c.id === currentClass)?.label || 'All Classes';

  // Load notifications from priorities
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchPriorities(api).then(d => {
      setNotifications((d?.items || []).slice(0, 6));
    }).catch(() => {});
  }, [api]);

  const setClassFilter = (classId) => {
    setGlobalClass(classId);
    // Also persist in URL for audit reconstructability
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (classId === 'all') {
        next.delete('class');
      } else {
        next.set('class', classId);
      }
      return next;
    });
    setClassOpen(false);
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!classOpen) return;
    const handler = () => setClassOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [classOpen]);

  const isObserver = ['regulator', 'investor'].includes(user?.role);
  const [session, setSession] = useState(null);
  useEffect(() => {
    if (!isObserver) return;
    api.get('/observers/self').then(r => setSession(r.data)).catch(() => {});
  }, [isObserver, api]);

  return (
    <>
    {isObserver && session?.observer && (
      <div className="sticky top-0 z-50 flex flex-wrap items-center gap-2 px-5 py-1.5 text-[11px]"
        data-testid="observer-banner"
        style={{ background: '#132A20', color: 'rgba(255,255,255,0.85)' }}>
        <span className="px-1.5 py-0.5 rounded-sm font-bold tracking-wider text-[9px]"
          style={{ background: 'rgba(74,222,128,0.18)', color: '#4ADE80' }}>READ-ONLY</span>
        <span><strong>{session.label} access</strong> — read-only. Cannot: {session.cannot}</span>
        <span className="ml-auto" data-testid="observer-expiry">
          {session.days_left} day{session.days_left === 1 ? '' : 's'} left · ends {String(session.expires_at).slice(0, 10)}
          {session.granted_by ? ` · granted by ${session.granted_by}` : ''}
        </span>
      </div>
    )}
    <div className="sticky top-0 z-40 flex items-center justify-between h-12 px-5 border-b" style={{ borderColor: 'rgba(136,150,168,0.15)', background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(12px)' }} data-testid="top-bar">
      {/* Search trigger */}
      <button onClick={onOpenSearch} aria-label="Open global search (Cmd+K)"
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors hover:bg-[#F5F7FA]"
        style={{ color: '#5C6B7A', border: '1px solid rgba(136,150,168,0.2)' }}
        data-testid="search-trigger">
        <Search className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Search...</span>
        <kbd className="hidden sm:inline text-[10px] ml-4 px-1.5 py-0.5 rounded" style={{ background: '#F5F7FA', color: '#5C6B7A', border: '1px solid rgba(136,150,168,0.15)' }}>
          ⌘K
        </kbd>
      </button>

      <div className="flex items-center gap-3">
        {/* Class Filter */}
        <div className="relative" onClick={e => e.stopPropagation()}>
          <button onClick={() => setClassOpen(!classOpen)} aria-label="Filter by insurance class" aria-expanded={classOpen}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors hover:bg-[#F5F7FA]"
            style={{ color: currentClass === 'all' ? '#5C6B7A' : '#1A4C7C', border: `1px solid ${currentClass === 'all' ? 'rgba(136,150,168,0.2)' : 'rgba(26,76,124,0.3)'}` }}
            data-testid="class-filter-trigger">
            <Filter className="w-3.5 h-3.5" />
            <span>{currentClassLabel}</span>
            <ChevronDown className="w-3 h-3" />
          </button>

          {classOpen && (
            <div className="absolute right-0 mt-1 w-56 py-1 bg-white rounded-lg shadow-lg z-50" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
              {INSURANCE_CLASSES.map(cls => (
                <button key={cls.id} onClick={() => setClassFilter(cls.id)}
                  className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${currentClass === cls.id ? 'bg-[#F0F4FF] font-medium' : 'hover:bg-[#F5F7FA]'}`}
                  style={{ color: currentClass === cls.id ? '#1A4C7C' : '#0D1B2A' }}
                  data-testid={`class-option-${cls.id}`}>
                  {cls.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative" onClick={e => e.stopPropagation()}>
          <button onClick={() => setNotifOpen(!notifOpen)} aria-label={`Notifications${notifications.length ? ` (${notifications.length} new)` : ''}`} aria-expanded={notifOpen} className="relative p-2 rounded-md hover:bg-[#F5F7FA] transition-colors" data-testid="notifications-btn">
            <Bell className="w-4 h-4" style={{ color: '#5C6B7A' }} />
            {notifications.length > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-[9px] font-bold text-white flex items-center justify-center" style={{ background: '#E24B4A' }}>
                {notifications.length}
              </span>
            )}
          </button>
          {notifOpen && (
            <div className="absolute right-0 mt-1 w-80 bg-white rounded-lg shadow-lg z-50" style={{ border: '1px solid rgba(136,150,168,0.2)' }}>
              <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
                <span className="text-xs font-semibold" style={{ color: '#0D1B2A' }}>Notifications</span>
                <button onClick={() => setNotifOpen(false)} aria-label="Close notifications"><X className="w-3.5 h-3.5" style={{ color: '#5C6B7A' }} /></button>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-center" style={{ color: '#5C6B7A' }}>All clear</p>
                ) : notifications.map((n, i) => {
                  const colors = { critical: '#E24B4A', high: '#E8A020', medium: '#00B4D8', low: '#5C6B7A', info: '#1D9E75' };
                  return (
                    <div key={i} className="px-3 py-2 border-b hover:bg-[#F5F7FA] transition-colors cursor-pointer" style={{ borderColor: 'rgba(136,150,168,0.08)', borderLeft: `3px solid ${colors[n.priority] || '#5C6B7A'}` }}>
                      <p className="text-xs font-medium" style={{ color: '#0D1B2A' }}>{n.title}</p>
                      <p className="text-[10px]" style={{ color: '#5C6B7A' }}>{n.subtitle}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* User avatar */}
        <div className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white" style={{ background: '#2A6FC4' }}>
          {user?.name?.charAt(0) || 'U'}
        </div>
      </div>
    </div>
  </>
  );
}
